import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
  Card,
  Typography,
  Box,
  CardMedia,
  IconButton,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import SamplePreview from "../../assets/images/placeholder-img.png";
import DeleteIcon from "../../assets/images/delete-icon.svg";

const useStyles = makeStyles((theme) => ({
  card: {
    border: `0.3px solid ${theme.palette.primary75.main}`,
    borderRadius: `6px !important`,
    padding: theme.spacing(1.5),
    boxShadow: "none !important",
    cursor: "pointer",
    display: "flex",
    gap: theme.spacing(2),
    "&.active": {
      border: `1px solid ${theme.palette.secondary.main}`,
      backgroundColor: theme.palette.background.secondaryLight,
    },
    "&.dragging": {
      border: `1px solid ${theme.palette.secondary.main}`,
      background: "rgb(235,235,235)",
    },
  },
  img: {
    height: 76,
    width: `142px !important`,
    border: `0.3px solid ${theme.palette.primary.main}`,
    borderRadius: 3,
  },
  title: {
    fontWeight: 600,
  },
  vertLine: {
    height: 20,
    border: `1px dashed ${theme.palette.primary35.main}`,
    width: 0,
  },
  duration: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
}));

const DomCard: React.FC<{
  id: string;
  name: string;
  eventsCount: number;
  hasNext: boolean;
  preview?: string;
  active: boolean;
  isDragging?: boolean;
  handlePreviewClick: () => void;
  handleClick: () => void;
  handleRemoveClick?: () => void;
}> = ({
  id,
  name,
  eventsCount,
  preview,
  hasNext,
  handlePreviewClick,
  handleClick,
  handleRemoveClick,
  active,
  isDragging,
}) => {
  const classes = useStyles();
  const ref = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    if (active) {
      ref?.current?.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [active]);

  return (
    <Box ref={ref}>
      <Card
        classes={{
          root: clsx(
            classes.card,
            active ? "active" : "",
            isDragging ? "dragging" : ""
          ),
        }}
        data-testid="snapshot-card-test-id"
        onClick={handleClick}
      >
        <CardMedia
          component="img"
          image={preview || SamplePreview}
          alt="snapshot"
          classes={{
            root: classes.img,
          }}
          onClick={handlePreviewClick}
        />
        <Box>
          <Box mb={0.5}>
            <Typography variant="body2" color="primary">
              {name}
            </Typography>
          </Box>
          <Typography variant="body2" color="primary">
            {eventsCount} Events found
          </Typography>
        </Box>
        {handleRemoveClick && (
          <Box ml={"auto"}>
            <Tooltip title={"Remove"}>
              <IconButton
                sx={{ padding: 0.5 }}
                onClick={handleRemoveClick}
                data-testid="remove-selected-snapshot"
              >
                <img src={DeleteIcon} alt="close" height="24" width="24" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Card>
      {hasNext && (
        <Box width="fit-content" ml={5}>
          <Box className={classes.duration}>
            <Box className={classes.vertLine} />

            <Box className={classes.vertLine} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

DomCard.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  eventsCount: PropTypes.number.isRequired,
  preview: PropTypes.string,
  hasNext: PropTypes.bool.isRequired,
  handlePreviewClick: PropTypes.func.isRequired,
  handleRemoveClick: PropTypes.func,
  active: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool,
  handleClick: PropTypes.func.isRequired,
};

export default DomCard;
